import './App.css';

function PrivacyPolicy() {
  return (
    <main className="prose-page">
      <header className="prose-head">
        <span className="eyebrow">Legal</span>
        <h1 className="prose-title">
          Privacy <span className="gradient-text">Policy</span>
        </h1>
        <p className="prose-meta">Last updated: April 25, 2026</p>
      </header>

      <article className="prose">
        <section>
          <h2>1. Introduction</h2>
          <p>
            Noorvia ("App", "we", "our", "us") is an Islamic lifestyle and productivity application.
            We are committed to protecting your privacy and being transparent about how your data is
            handled. This Privacy Policy explains what data the App collects, how it is used, stored,
            and shared (or not shared).
          </p>
          <p className="callout">
            <strong>In short:</strong> We do not collect, store, or sell your personal data.
            Everything stays on your device and your private iCloud account.
          </p>
        </section>

        <section>
          <h2>2. Information We Do NOT Collect</h2>
          <p>We want to be clear about what we do <strong>not</strong> do:</p>
          <ul>
            <li>We do <strong>not</strong> collect personally identifiable information (name, email, phone number, etc.)</li>
            <li>We do <strong>not</strong> use analytics, telemetry, or tracking frameworks</li>
            <li>We do <strong>not</strong> use advertising SDKs or ad networks</li>
            <li>We do <strong>not</strong> use tracking pixels, fingerprinting, or cookies</li>
            <li>We do <strong>not</strong> sell, rent, share, or transmit any user data to third parties</li>
            <li>We do <strong>not</strong> operate any servers that receive or store your data</li>
            <li>We do <strong>not</strong> collect usage statistics, crash reports, or behavioral data</li>
            <li>We do <strong>not</strong> use any third-party data collection SDKs</li>
          </ul>
        </section>

        <section>
          <h2>3. Data Stored on Your Device</h2>
          <p>
            The App stores the following data locally on your device and synced via your personal
            iCloud account:
          </p>
          <h3>3.1 Religious Practice Data</h3>
          <ul>
            <li>Prayer logs and completion records</li>
            <li>Quran reading progress, bookmarks, notes, and collections</li>
            <li>Hadith bookmarks, notes, and folder organization</li>
            <li>Dhikr (remembrance) session counts and history</li>
            <li>Fasting logs and records</li>
            <li>Zakat (charitable giving) tracking records</li>
            <li>Sunnah (voluntary) prayer tracking</li>
            <li>Spiritual checklist progress, streaks, and achievements</li>
            <li>Dua (supplication) favorites</li>
          </ul>
          <h3>3.2 App Preferences and Settings</h3>
          <ul>
            <li>Display preferences (accent color, theme settings)</li>
            <li>Prayer time calculation method and notification preferences</li>
            <li>Quran reading settings (font size, translation edition, reciter)</li>
            <li>Notification sound preferences</li>
            <li>Gender preference (used solely for displaying gender-appropriate prayer instructions)</li>
            <li>User name (optional, used only for display within the App)</li>
            <li>Onboarding completion status</li>
          </ul>
          <h3>3.3 Menstruation and Ritual Purity Data</h3>
          <ul>
            <li>Menstruation and impurity tracking logs (used solely for Islamic ritual purity calculations)</li>
            <li>This data is stored locally and in your private iCloud account only</li>
            <li>This data is <strong>never</strong> transmitted to any server or third party</li>
          </ul>
          <p>All of the above data is stored using:</p>
          <ul>
            <li><strong>Apple's SwiftData with CloudKit</strong> — for log and record data (synced via your private iCloud database)</li>
            <li><strong>Apple's UserDefaults with iCloud Key-Value Storage</strong> — for settings and preferences</li>
          </ul>
          <p>We have no access to your iCloud data. Only devices signed into your Apple ID can access it.</p>
        </section>

        <section>
          <h2>4. Location Data</h2>
          <h3>4.1 What We Access</h3>
          <p>The App may request "When In Use" location permission to:</p>
          <ul>
            <li>Calculate accurate prayer times for your location</li>
            <li>Determine the Qibla (direction of prayer) compass heading</li>
          </ul>
          <h3>4.2 How Location Data Is Used</h3>
          <ul>
            <li>Location coordinates are processed <strong>on-device</strong> for prayer time calculations</li>
            <li>Approximate coordinates may be included in API requests to the Aladhan prayer times service to fetch prayer time data</li>
            <li>Location data is used for Qibla direction calculation entirely on-device</li>
          </ul>
          <h3>4.3 What We Do NOT Do With Location Data</h3>
          <ul>
            <li>We do <strong>not</strong> store your location history</li>
            <li>We do <strong>not</strong> track your movements</li>
            <li>We do <strong>not</strong> transmit location data to our own servers (we do not operate any)</li>
            <li>We do <strong>not</strong> share location data with third parties for any purpose</li>
            <li>We do <strong>not</strong> use location data for advertising or profiling</li>
          </ul>
          <h3>4.4 Your Control</h3>
          <ul>
            <li>Location access is entirely optional</li>
            <li>You can deny or revoke location permission at any time in your device's Settings</li>
            <li>Without location access, prayer times and Qibla features will not function, but all other App features will work normally</li>
          </ul>
        </section>

        <section>
          <h2>5. Third-Party Services</h2>
          <p>The App communicates with the following third-party services for content delivery only:</p>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Service</th>
                  <th>Purpose</th>
                  <th>Data Sent</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Al-Quran Cloud API (api.alquran.cloud)</td>
                  <td>Quranic text, translations, audio metadata</td>
                  <td>Surah/ayah numbers, translation edition</td>
                </tr>
                <tr>
                  <td>Islamic Network CDN (cdn.islamic.network)</td>
                  <td>Quran audio recitation files</td>
                  <td>Audio file identifiers</td>
                </tr>
                <tr>
                  <td>Aladhan API (api.aladhan.com)</td>
                  <td>Islamic calendar date conversion</td>
                  <td>Date parameters, approximate coordinates for prayer times</td>
                </tr>
              </tbody>
            </table>
          </div>
          <h3>Important Notes:</h3>
          <ul>
            <li>These are <strong>read-only</strong> requests — we only fetch content, we never send personal data</li>
            <li>No user accounts, identifiers, or personal information are transmitted</li>
            <li>We do not control these third-party services and recommend reviewing their own privacy policies</li>
            <li>These services may log standard web request information (IP address, timestamps) as part of their own server operations — this is outside our control</li>
          </ul>
        </section>

        <section>
          <h2>6. Notifications</h2>
          <ul>
            <li>The App uses <strong>local notifications only</strong> for prayer time reminders</li>
            <li>We do <strong>not</strong> use push notifications from external servers</li>
            <li>We do <strong>not</strong> send marketing, promotional, or advertising notifications</li>
            <li>Notification permissions can be managed in your device's Settings at any time</li>
          </ul>
        </section>

        <section>
          <h2>7. In-App Purchases</h2>
          <ul>
            <li>The in-app subscription ("Noorvia Gold") is processed entirely by Apple's App Store</li>
            <li>We do <strong>not</strong> collect, process, or store any payment information</li>
            <li>Purchase status is verified on-device using Apple's StoreKit framework</li>
            <li>No purchase data is transmitted to any server we operate</li>
          </ul>
        </section>

        <section>
          <h2>8. Children's Privacy</h2>
          <ul>
            <li>The App is not designed for or marketed to children under 13</li>
            <li>We do not knowingly collect any personal information from children</li>
            <li>The App does not contain advertising, social features, or any mechanism for children to share personal information</li>
            <li>Parents and guardians should supervise children's use of the App</li>
          </ul>
        </section>

        <section>
          <h2>9. Data Retention and Deletion</h2>
          <h3>9.1 Data Retention</h3>
          <ul>
            <li>All data remains on your device and in your private iCloud account</li>
            <li>We have no ability to access, view, or retrieve your data</li>
            <li>Data persists as long as the App is installed and iCloud sync is active</li>
          </ul>
          <h3>9.2 Data Deletion</h3>
          <p>You can delete your data by:</p>
          <ul>
            <li><strong>Deleting the App</strong> — removes all local data from that device</li>
            <li><strong>Disabling iCloud sync</strong> — stops cross-device synchronization</li>
            <li><strong>Signing out of iCloud</strong> — removes synced data from the device</li>
            <li><strong>Using iOS Settings &gt; General &gt; iPhone Storage &gt; Noorvia &gt; Delete App</strong> — removes all App data</li>
          </ul>
          <p>
            Since we do not store your data on any server, deleting the App and disabling iCloud
            sync permanently removes all your data. <strong>There is no way to recover data after
            deletion.</strong>
          </p>
        </section>

        <section>
          <h2>10. Data Security</h2>
          <ul>
            <li>All data is stored using Apple's native frameworks (SwiftData, UserDefaults, iCloud)</li>
            <li>iCloud data is encrypted in transit and at rest by Apple</li>
            <li>On-device data is protected by your device's security features (passcode, Face ID, Touch ID)</li>
            <li>We do not implement custom authentication or account systems</li>
            <li>We do not store passwords, tokens, or credentials</li>
          </ul>
        </section>

        <section>
          <h2>11. International Data Transfers</h2>
          <ul>
            <li>Your data syncs between your own devices via Apple's iCloud infrastructure</li>
            <li>iCloud data may be stored in Apple's data centers in various countries, subject to Apple's privacy policy</li>
            <li>We do not independently transfer any data internationally</li>
            <li>API requests to third-party services (Section 5) are standard HTTPS requests that may route through international servers</li>
          </ul>
        </section>

        <section>
          <h2>12. Your Rights</h2>
          <p>Depending on your jurisdiction, you may have the following rights:</p>
          <ul>
            <li><strong>Right to Access</strong> — All your data is visible within the App on your device</li>
            <li><strong>Right to Deletion</strong> — You can delete the App to remove all data (see Section 9.2)</li>
            <li><strong>Right to Portability</strong> — The App does not currently offer data export functionality</li>
            <li><strong>Right to Opt-Out</strong> — There is no data collection to opt out of; you can disable location access and notifications independently</li>
          </ul>
          <p>
            Since we do not collect or store your personal data on any server, most traditional data
            subject requests (access, correction, deletion) are inherently satisfied — your data is
            entirely under your control on your own devices.
          </p>
        </section>

        <section>
          <h2>13. Changes to This Privacy Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. Changes will be reflected in the
            "Last Updated" date at the top of this document. Continued use of the App after changes
            constitutes acceptance of the updated policy.
          </p>
          <p>We will endeavor to notify users of significant changes through the App or App Store update notes.</p>
        </section>

        <section>
          <h2>14. California Privacy Rights (CCPA)</h2>
          <p>For California residents:</p>
          <ul>
            <li>We do <strong>not</strong> sell personal information</li>
            <li>We do <strong>not</strong> collect personal information for commercial purposes</li>
            <li>We do <strong>not</strong> use personal information for targeted advertising</li>
            <li>The App qualifies for the small business exemption as we collect no personal information</li>
          </ul>
        </section>

        <section>
          <h2>15. European Privacy Rights (GDPR)</h2>
          <p>For users in the European Economic Area:</p>
          <ul>
            <li><strong>Legal Basis</strong> — The App processes data (locally) based on your consent (granting permissions) and legitimate interest (providing App functionality)</li>
            <li><strong>Data Controller</strong> — Since data is stored only on your devices and your iCloud account, you are effectively the controller of your own data</li>
            <li><strong>Data Protection Officer</strong> — Not applicable as we do not process personal data on our servers</li>
            <li><strong>Right to Lodge a Complaint</strong> — You may contact your local data protection authority if you have concerns</li>
          </ul>
        </section>

        <section>
          <h2>16. Contact Information</h2>
          <p>
            If you have any questions, concerns, or requests regarding this Privacy Policy, please
            contact us at:
          </p>
          <p className="contact">
            <a href="mailto:noorviasupport@gmail.com">noorviasupport@gmail.com</a>
          </p>
          <p>We endeavor to respond to all privacy-related inquiries in a timely manner.</p>
        </section>
      </article>
    </main>
  );
}

export default PrivacyPolicy;
